import { supabase } from './supabase';

export interface UserPreferences {
  show_repair_tasks?: boolean;
  // Weitere Einstellungen können hier hinzugefügt werden
  view_mode?: 'list' | 'kanban';
  status_filter?: string;
  theme?: 'light' | 'dark';
  // Sortierungseinstellungen
  sort_by?: 'due_date' | 'created_at' | 'customer' | 'status';
  sort_order?: 'asc' | 'desc';
  // Feature Toggles
  enable_inventory_management?: boolean;
  enable_document_management?: boolean;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  show_repair_tasks: true,
  view_mode: 'kanban',
  status_filter: 'ALL',
  theme: 'light',
  // Default Sortierung: Aufträge mit dem nächsten Fertigstellungsdatum zuerst
  sort_by: 'due_date',
  sort_order: 'asc',
  // Feature Toggles standardmäßig deaktiviert
  enable_inventory_management: false,
  enable_document_management: false
};

/**
 * Lädt die Benutzereinstellungen aus der Datenbank
 */
export async function getUserPreferences(userId: string): Promise<UserPreferences> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('user_preferences')
      .eq('id', userId)
      .single();

    if (error) {
      console.warn('[UserPreferences] Failed to load preferences:', error);
      
      // Spezielle Behandlung für fehlende Spalte
      if (error.message.includes('column "user_preferences" does not exist')) {
        console.warn('[UserPreferences] ⚠️ user_preferences column does not exist, using defaults');
        // Lokale Einstellungen verwenden falls verfügbar
        const localPrefs = getLocalPreferences(userId);
        return localPrefs;
      }
      
      return DEFAULT_PREFERENCES;
    }

    // Merge mit Default-Werten
    const preferences = { ...DEFAULT_PREFERENCES, ...data.user_preferences };
    return preferences;
  } catch (error) {
    console.warn('[UserPreferences] Error loading preferences:', error);
    
    // Fallback zu lokalen Einstellungen
    const localPrefs = getLocalPreferences(userId);
    return localPrefs;
  }
}

/**
 * Speichert eine einzelne Benutzereinstellung
 */
export async function updateUserPreference<K extends keyof UserPreferences>(
  userId: string,
  key: K,
  value: UserPreferences[K]
): Promise<boolean> {
  try {
    // Aktuelle Einstellungen laden
    const currentPreferences = await getUserPreferences(userId);
    
    // Neue Einstellung hinzufügen/aktualisieren
    const updatedPreferences = {
      ...currentPreferences,
      [key]: value
    };

    const { error } = await supabase
      .from('profiles')
      .update({ user_preferences: updatedPreferences })
      .eq('id', userId);

    if (error) {
      console.error('[UserPreferences] Failed to update preference:', error);
      
      // Spezielle Behandlung für fehlende Spalte
      if (error.message.includes('column "user_preferences" does not exist')) {
        console.warn('[UserPreferences] ⚠️ user_preferences column does not exist, saving locally only');
        // Nur lokal speichern
        setLocalPreferences(userId, updatedPreferences);
        return true; // Als "erfolgreich" markieren
      }
      
      return false;
    }

    console.log(`[UserPreferences] Updated ${String(key)} to:`, value);
    
    // Auch lokal speichern für Konsistenz
    setLocalPreferences(userId, updatedPreferences);
    
    return true;
  } catch (error) {
    console.error('[UserPreferences] Error updating preference:', error);
    
    // Fallback: Nur lokal speichern
    try {
      const currentPreferences = getLocalPreferences(userId);
      const updatedPreferences = {
        ...currentPreferences,
        [key]: value
      };
      setLocalPreferences(userId, updatedPreferences);
      console.warn('[UserPreferences] ⚠️ Saved preference locally only due to error');
      return true;
    } catch (localError) {
      console.error('[UserPreferences] Failed to save locally:', localError);
      return false;
    }
  }
}

/**
 * Speichert mehrere Benutzereinstellungen auf einmal
 */
export async function updateUserPreferences(
  userId: string,
  preferences: Partial<UserPreferences>
): Promise<boolean> {
  try {
    // Aktuelle Einstellungen laden
    const currentPreferences = await getUserPreferences(userId);
    
    // Alle Einstellungen mergen
    const updatedPreferences = {
      ...currentPreferences,
      ...preferences
    };

    const { error } = await supabase
      .from('profiles')
      .update({ user_preferences: updatedPreferences })
      .eq('id', userId);

    if (error) {
      console.error('[UserPreferences] Failed to update preferences:', error);
      
      // Spezielle Behandlung für fehlende Spalte
      if (error.message.includes('column "user_preferences" does not exist')) {
        console.warn('[UserPreferences] ⚠️ user_preferences column does not exist, saving locally only');
        setLocalPreferences(userId, updatedPreferences);
        return true;
      }
      
      return false;
    }

    console.log('[UserPreferences] Updated preferences:', preferences);
    
    // Auch lokal speichern
    setLocalPreferences(userId, updatedPreferences);
    
    return true;
  } catch (error) {
    console.error('[UserPreferences] Error updating preferences:', error);
    
    // Fallback: Nur lokal speichern
    try {
      const currentPreferences = getLocalPreferences(userId);
      const updatedPreferences = {
        ...currentPreferences,
        ...preferences
      };
      setLocalPreferences(userId, updatedPreferences);
      console.warn('[UserPreferences] ⚠️ Saved preferences locally only due to error');
      return true;
    } catch (localError) {
      console.error('[UserPreferences] Failed to save locally:', localError);
      return false;
    }
  }
}

/**
 * Hook für lokalen Fallback (falls DB nicht verfügbar)
 * Speichert Einstellungen zusätzlich in localStorage
 */
export const localPreferenceKey = (userId: string) => `velodesk_prefs_${userId}`;

export function getLocalPreferences(userId: string): UserPreferences {
  try {
    const stored = localStorage.getItem(localPreferenceKey(userId));
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_PREFERENCES, ...parsed };
    }
  } catch (error) {
    console.warn('[UserPreferences] Failed to load local preferences:', error);
  }
  return DEFAULT_PREFERENCES;
}

export function setLocalPreferences(userId: string, preferences: UserPreferences): void {
  try {
    localStorage.setItem(localPreferenceKey(userId), JSON.stringify(preferences));
    console.log('[UserPreferences] ✅ Saved preferences locally:', preferences);
  } catch (error) {
    console.warn('[UserPreferences] Failed to save local preferences:', error);
  }
} 